/**
 * Resolves the IANA zone an event's times should be attributed to.
 *
 * The API returns the zone in more than one place and either may be null: an
 * event authored without an address carries only the top-level `timezone`, and
 * some older rows carry only the address's. `venue` is included because events
 * return the same venue/facility/address triple games do, and a venue-backed
 * event is authored against the venue's clock.
 *
 * Returns null when the event carries no zone at all — callers then attribute
 * the time to the viewer's own zone rather than inventing one, and suppress the
 * second line since there is no second clock to show.
 */
export interface EventTimezoneSource {
  timezone?: string | null
  venue?: { timezone?: string | null } | null
  address?: { timezone?: string | null } | null
}

const firstNonEmpty = (
  ...candidates: (string | null | undefined)[]
): string | null => {
  for (const candidate of candidates) {
    if (typeof candidate === 'string' && candidate.trim().length > 0) {
      return candidate
    }
  }
  return null
}

export const resolveEventTimezone = (
  event?: EventTimezoneSource | null
): string | null => {
  if (!event) {
    return null
  }

  return firstNonEmpty(
    event.timezone,
    event.venue?.timezone,
    event.address?.timezone
  )
}
